import * as React from 'react';
import { useLocation } from 'react-router-dom';
import { create } from 'zustand';

interface BreadcrumbLabelStore {
  /** Route path -> the text the breadcrumb should show instead of the raw URL segment. */
  labels: Record<string, string>;
  setLabel: (path: string, label: string) => void;
  releaseLabel: (path: string, label: string) => void;
}

export const useBreadcrumbLabelStore = create<BreadcrumbLabelStore>((set) => ({
  labels: {},

  setLabel: (path, label) =>
    set((state) => (state.labels[path] === label ? state : { labels: { ...state.labels, [path]: label } })),

  releaseLabel: (path, label) =>
    set((state) => {
      // Only drop our own entry. During a route change the incoming page can register
      // before the outgoing page's cleanup runs, so an unconditional delete could
      // remove a label that no longer belongs to us.
      if (state.labels[path] !== label) return state;
      const next = { ...state.labels };
      delete next[path];
      return { labels: next };
    }),
}));

/**
 * Replaces the breadcrumb text for the route the calling component is rendered at.
 *
 * Call it near the top of a detail component with something meaningful to the user:
 *
 *   useBreadcrumbLabel(instructor.rhnId);
 *
 * Pass undefined/null/'' while the entity is still loading — the raw URL segment stays
 * visible until a real value arrives, so there is no empty crumb mid-fetch. The override
 * is scoped to this route and released on unmount.
 *
 * Pass `path` to label a crumb other than the calling component's own — a detail page
 * whose parent segment does not read well title-cased has to name it itself, since the
 * overview that would otherwise register it is unmounted:
 *
 *   useBreadcrumbLabel('Client Executives', '/clientexecutives');
 */
export const useBreadcrumbLabel = (label?: string | number | null, path?: string) => {
  const { pathname } = useLocation();
  const text = label === undefined || label === null ? '' : String(label);

  // Key the override the same way AppLayout rebuilds each crumb's path (segments
  // rejoined), so a trailing or doubled slash in the URL still matches.
  const key = `/${(path ?? pathname).split('/').filter(Boolean).join('/')}`;

  React.useEffect(() => {
    if (!text) return;

    const { setLabel, releaseLabel } = useBreadcrumbLabelStore.getState();
    setLabel(key, text);

    return () => releaseLabel(key, text);
  }, [key, text]);
};
